import type { TransversaleDeFeuille } from '../partage/api/types';
import { debutDeLEtiquette, ouvrirLaFicheDe } from '../partage/vocabulaire';
import { dessinDuLien } from './dessinDesLiens';
import type { EtiquetteDuDehors } from './placeDesEtiquettes';
import './EtiquettesTransversales.css';

/**
 * Ce que dit un lien qui sort de la feuille, « Retient « Le passeport » · Bali » (depuis le
 * chantier liens-entre-bulles, ticket 03, selon sa sorte : « Attend », « Retient », « Sert à »,
 * « S'appuie sur », « Touche à »). Un « attend » rempli, ou un lien caché montré par A, en pâle.
 *
 * La carte ne dessine que les bulles de la feuille. Sans cette étiquette, une bulle qui
 * sert aussi dans un autre dossier, ou qui attend une bulle d'ailleurs, ne le montrait pas :
 * on la cochait sans savoir ce qu'elle libérait plus loin.
 *
 * Un clic ouvre la fiche de la bulle d'ailleurs, par-dessus la carte, sans la quitter (chantier
 * liens-entre-bulles, ticket 05, décision 12 du cadrage, la spec `carte` depuis le 2026-09-29).
 * Sans `onOuvrir` (pendant Lier ou Hiérarchiser), elle informe seulement, comme sur la planche.
 */
export function EtiquettesTransversales({
  etiquettes,
  onOuvrir,
}: {
  etiquettes: EtiquetteDuDehors<TransversaleDeFeuille>[];
  onOuvrir?: (id: string) => void;
}) {
  return (
    <>
      {etiquettes.map(({ point, transversale }) => {
        const morceaux = decoupe(transversale);
        if (!morceaux) return null;
        const classe = `tableau-transversale${dessinDuLien(transversale).pale ? ' tableau-transversale--pale' : ''}`;
        const contenu = (
          <>
            <span className="tableau-transversale-point" aria-hidden="true" />
            <span className="tableau-transversale-texte">
              {morceaux.debut}
              <span className="tableau-transversale-titre">{morceaux.titre}</span>
              {morceaux.fin}
            </span>
          </>
        );
        const cle = `${transversale.sorte}-${transversale.sourceId}-${transversale.cibleId}-${transversale.visible ? 'v' : 'c'}`;
        const dehors = transversale.dehors;
        if (!onOuvrir || !dehors) {
          return (
            <span key={cle} className={classe} data-sorte={transversale.sorte} style={{ left: point.x, top: point.y }} title={phrase(transversale) ?? undefined}>
              {contenu}
            </span>
          );
        }
        return (
          <button
            key={cle}
            type="button"
            // `carte-commandes` : le geste du regard (glisser la carte) ne prend pas ce clic.
            className={`${classe} tableau-transversale--ouvrable carte-commandes`}
            data-sorte={transversale.sorte}
            data-dehors={dehors.id}
            style={{ left: point.x, top: point.y }}
            title={`${phrase(transversale) ?? ''}
${ouvrirLaFicheDe(dehors.titre)}`}
            onClick={(e) => {
              e.stopPropagation();
              onOuvrir(dehors.id);
            }}
          >
            {contenu}
          </button>
        );
      })}
    </>
  );
}

/**
 * Le début dit la sorte et le côté (`debutDeLEtiquette`) : « Retient » quand la bulle d'ici est
 * attendue par l'autre, « Attend » quand c'est l'inverse, « Sert à » et « S'appuie sur »,
 * « Touche à ».
 *
 * Le titre est entre guillemets, comme partout où l'app cite une bulle : la planche écrit
 * « sert aussi au passeport », mais un titre est du contenu variable, et « sert aussi à
 * La purge du droit de préemption » ne se lirait pas.
 *
 * Trois morceaux, parce qu'un seul peut se couper : le titre de la bulle. Le début dit le
 * sens du lien, la fin dit le dossier de l'autre bout (« · Bali ») : couper l'un ou l'autre
 * fait perdre ce que l'étiquette est là pour dire.
 */
export function decoupe({
  sens,
  sorte,
  dehors,
}: TransversaleDeFeuille): { debut: string; titre: string; fin: string } | null {
  if (!dehors || sens === 'dedans') return null;
  const debut = debutDeLEtiquette(sorte, sens);
  const fin = dehors.dossier ? ` » · ${dehors.dossier}` : ' »';
  return { debut, titre: dehors.titre, fin };
}

/** La phrase entière, pour le survol quand le titre a dû se couper. */
export function phrase(transversale: TransversaleDeFeuille): string | null {
  const morceaux = decoupe(transversale);
  return morceaux ? morceaux.debut + morceaux.titre + morceaux.fin : null;
}

/**
 * La largeur qu'une étiquette prendra, pour que la carte lui trouve une place avant de la
 * dessiner. Une estimation, volontairement large : Figtree en 12 px fait 6 px par signe en
 * moyenne, on en compte 6,6. Trop large, l'étiquette s'écarte un peu plus ; trop étroite,
 * elle mordrait sur une bulle.
 *
 * L'étiquette n'a pas de largeur maximale, comme sur la planche : seul le titre est plafonné
 * (`.tableau-transversale-titre`, `max-width`), le début et le dossier comptent en entier.
 */
export const LARGEUR_MAX_TITRE = 240;
const PAR_SIGNE = 6.6;
export function largeurEstimee(transversale: TransversaleDeFeuille): number {
  const morceaux = decoupe(transversale);
  if (!morceaux) return 0;
  const titre = Math.min(LARGEUR_MAX_TITRE, Math.ceil(morceaux.titre.length * PAR_SIGNE));
  const reste = Math.ceil((morceaux.debut.length + morceaux.fin.length) * PAR_SIGNE);
  // 12 + 12 de marge intérieure, 6 de point, 8 d'écart, 2 de filet.
  return titre + reste + 40;
}
